import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface WorkflowToolbarProps {
  // Workflow metadata
  workflowName: string;
  workflowDescription: string;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  
  // Actions
  onSave: () => void;
  onBack: () => void;
  onTest?: () => void;
  canTest?: boolean;
  isSaving: boolean;
  isTesting?: boolean;
  
  // View controls
  viewMode: 'default' | 'timeline' | 'parallel';
  onViewModeChange: (mode: 'default' | 'timeline' | 'parallel') => void;
  showCriticalPath: boolean;
  onShowCriticalPathChange: (show: boolean) => void;
  showMetrics: boolean;
  onShowMetricsChange: (show: boolean) => void;
  
  // History controls
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  
  // Validation status
  workflowValidation?: any;
  
  // Layout options
  layout?: 'compact' | 'full' | 'minimal';
  showMetadata?: boolean;
  showViewControls?: boolean;
  showHistoryControls?: boolean;
  
  // Clear selection control (keep this one)
  onClearSelection?: () => void;

  // Auto-save
  saveStatus?: 'saved' | 'saving' | 'unsaved';
  autoSave?: boolean;
  onAutoSaveToggle?: (enabled: boolean) => void;
}

export const WorkflowToolbar: React.FC<WorkflowToolbarProps> = ({
  workflowName,
  workflowDescription,
  onNameChange,
  onDescriptionChange,
  onSave,
  onBack,
  onTest,
  canTest,
  isSaving,
  isTesting,
  viewMode,
  onViewModeChange,
  showCriticalPath,
  onShowCriticalPathChange,
  showMetrics,
  onShowMetricsChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  workflowValidation,
  layout = 'full',
  showMetadata = true,
  showViewControls = true,
  showHistoryControls = true,
  onClearSelection,
  saveStatus,
  autoSave,
  onAutoSaveToggle
}) => {
  const { darkMode } = useTheme();

  // Compact layout - single row with essential controls
  if (layout === 'compact') {
    return (
      <div className={`flex items-center justify-between p-2 border-b ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center space-x-3">
          <button
            onClick={onBack}
            className={`px-2 py-1 text-xs rounded border ${
              darkMode
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            ← Back
          </button>
          
          {showMetadata && (
            <input
              type="text"
              className={`px-2 py-1 text-sm border rounded max-w-xs ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              value={workflowName}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Workflow name"
            />
          )}
        </div>

        <div className="flex items-center space-x-2">
          {showHistoryControls && (
            <>
              <button
                onClick={onUndo}
                disabled={!canUndo}
                className={`px-1 py-1 text-xs rounded ${
                  canUndo 
                    ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title="Undo (Ctrl+Z)"
              >
                ↶
              </button>
              <button
                onClick={onRedo}
                disabled={!canRedo}
                className={`px-1 py-1 text-xs rounded ${
                  canRedo 
                    ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title="Redo (Ctrl+Y)"
              >
                ↷
              </button>
            </>
          )}
          
          <button
            className={`px-3 py-1 text-sm font-medium ${
              isSaving 
                ? 'bg-gray-500 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700'
            } text-white rounded flex items-center justify-center whitespace-nowrap`}
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  // Minimal layout - just essential actions
  if (layout === 'minimal') {
    return (
      <div className={`flex items-center justify-between p-1 ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <button
          onClick={onBack}
          className={`px-2 py-1 text-xs rounded ${
            darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          ← Back
        </button>
        
        <button
          className={`px-2 py-1 text-xs ${
            isSaving 
              ? 'bg-gray-500 cursor-not-allowed' 
              : 'bg-green-600 hover:bg-green-700'
          } text-white rounded`}
          onClick={onSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    );
  }

  // Full layout - two rows with dark green theme and glow effects
  return (
    <div className={`sticky top-0 z-50 border-b ${
      darkMode ? 'bg-gray-900 border-green-800' : 'bg-gray-50 border-green-200'
    } shadow-lg`}>
      {/* First row: Navigation, title, and action buttons */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-green-700/20">
        {/* Left section: Navigation and title */}
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className={`px-3 py-2 text-sm rounded border transition-all duration-200 ${
              darkMode
                ? 'border-green-700 text-green-300 hover:bg-green-800 hover:shadow-green-500/20 hover:shadow-lg'
                : 'border-green-300 text-green-700 hover:bg-green-50 hover:shadow-green-300/30 hover:shadow-md'
            }`}
          >
            ← Back
          </button>
          
          <h2 className={`text-lg font-semibold ${
            darkMode ? 'text-green-300' : 'text-green-800'
          }`}>
            Edit Workflow
          </h2>
        </div>

        {/* Right section: History controls and save button */}
        <div className="flex items-center space-x-4">
          {/* History controls */}
          {showHistoryControls && (
            <div className="flex items-center space-x-1">
              <button
                onClick={onUndo}
                disabled={!canUndo}
                className={`px-2 py-1 text-xs rounded transition-all duration-200 ${
                  canUndo 
                    ? darkMode
                      ? 'bg-green-800 hover:bg-green-700 text-white hover:shadow-green-500/30 hover:shadow-lg'
                      : 'bg-green-600 hover:bg-green-700 text-white hover:shadow-green-400/40 hover:shadow-md'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title="Undo (Ctrl+Z)"
              >
                ↶
              </button>
              <button
                onClick={onRedo}
                disabled={!canRedo}
                className={`px-2 py-1 text-xs rounded transition-all duration-200 ${
                  canRedo 
                    ? darkMode
                      ? 'bg-green-800 hover:bg-green-700 text-white hover:shadow-green-500/30 hover:shadow-lg'
                      : 'bg-green-600 hover:bg-green-700 text-white hover:shadow-green-400/40 hover:shadow-md'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title="Redo (Ctrl+Y)"
              >
                ↷
              </button>
            </div>
          )}


          {/* Validation status */}
          {workflowValidation && (
            <div className="flex items-center space-x-1">
              {workflowValidation.errors.length > 0 && (
                <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded shadow-sm">
                  {workflowValidation.errors.length} error{workflowValidation.errors.length > 1 ? 's' : ''}
                </span>
              )}
              {workflowValidation.warnings.length > 0 && (
                <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded shadow-sm">
                  {workflowValidation.warnings.length} warning{workflowValidation.warnings.length > 1 ? 's' : ''}
                </span>
              )}
              {workflowValidation.isValid && workflowValidation.errors.length === 0 && workflowValidation.warnings.length === 0 && (
                <span className={`text-xs px-2 py-1 rounded shadow-sm ${
                  darkMode 
                    ? 'text-green-300 bg-green-900/50 border border-green-700' 
                    : 'text-green-700 bg-green-100 border border-green-200'
                }`}>
                  Valid
                </span>
              )}
            </div>
          )}

          {/* Test button */}
          {onTest && (
            <button
              onClick={onTest}
              disabled={!canTest || isTesting || isSaving}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                transition-all duration-200 shadow-lg
                ${!canTest || isTesting || isSaving
                  ? 'opacity-50 cursor-not-allowed bg-gray-600 text-gray-300'
                  : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500 hover:shadow-green-500/40 hover:-translate-y-0.5'
                }
              `}
              title={!canTest ? "Save workflow first to enable testing" : "Test this workflow"}
            >
              {isTesting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  <span>Starting...</span>
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Test Workflow</span>
                </>
              )}
            </button>
          )}

          {/* Save status indicator */}
          {saveStatus && (
            <span className={`text-xs ${
              saveStatus === 'saving'  ? 'text-gray-400' :
              saveStatus === 'unsaved' ? 'text-amber-400' :
              'text-green-400'
            }`}>
              {saveStatus === 'saving'  ? 'Saving...' :
               saveStatus === 'unsaved' ? '● Unsaved' :
               '✓ Saved'}
            </span>
          )}

          {/* Auto-save toggle */}
          {onAutoSaveToggle !== undefined && (
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={autoSave ?? true}
                onChange={e => onAutoSaveToggle(e.target.checked)}
                className="rounded"
              />
              <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                Auto-save
              </span>
            </label>
          )}

          {/* Save button */}
          <button
            className={`px-4 py-2 text-sm font-medium rounded flex items-center justify-center whitespace-nowrap transition-all duration-200 ${
              isSaving 
                ? 'bg-gray-500 cursor-not-allowed' 
                : darkMode
                  ? 'bg-green-700 hover:bg-green-600 text-white hover:shadow-green-500/40 hover:shadow-xl'
                  : 'bg-green-600 hover:bg-green-700 text-white hover:shadow-green-400/50 hover:shadow-lg'
            }`}
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save Workflow'
            )}
          </button>

        </div>
      </div>

      {/* Second row: Metadata inputs and view controls */}
      <div className="flex items-center justify-between px-3 py-2 space-x-4">
        {/* Left section: Metadata inputs with more space */}
        {showMetadata && (
          <div className="flex items-center space-x-4 flex-1 max-w-4xl">
            <div className="flex-1 min-w-0">
              <input
                type="text"
                className={`w-full px-3 py-2 text-base border-0 border-b-2 bg-transparent transition-all duration-200 focus:outline-none ${
                  darkMode
                    ? 'border-green-700/50 text-green-100 placeholder-green-400/60 focus:border-green-500 focus:shadow-green-500/20 focus:shadow-lg hover:border-green-600/70'
                    : 'border-green-300/50 text-green-900 placeholder-green-600/60 focus:border-green-500 focus:shadow-green-300/30 focus:shadow-md hover:border-green-400/70'
                }`}
                value={workflowName}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="Click to edit workflow name..."
              />
            </div>
            <div className="flex-1 min-w-0">
              <input
                type="text"
                className={`w-full px-3 py-2 text-base border-0 border-b-2 bg-transparent transition-all duration-200 focus:outline-none ${
                  darkMode
                    ? 'border-green-700/50 text-green-100 placeholder-green-400/60 focus:border-green-500 focus:shadow-green-500/20 focus:shadow-lg hover:border-green-600/70'
                    : 'border-green-300/50 text-green-900 placeholder-green-600/60 focus:border-green-500 focus:shadow-green-300/30 focus:shadow-md hover:border-green-400/70'
                }`}
                value={workflowDescription}
                onChange={(e) => onDescriptionChange(e.target.value)}
                placeholder="Click to add description..."
              />
            </div>
          </div>
        )}

        {/* Right section: View controls */}
        {showViewControls && (
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className={`text-xs font-medium ${
                darkMode ? 'text-green-300' : 'text-green-700'
              }`}>
                View:
              </label>
              <select
                className={`border rounded px-2 py-1 text-xs transition-all duration-200 ${
                  darkMode
                    ? 'bg-gray-800 border-green-700 text-white focus:border-green-500 focus:shadow-green-500/20 focus:shadow-lg'
                    : 'bg-white border-green-300 text-gray-900 focus:border-green-500 focus:shadow-green-300/30 focus:shadow-md'
                }`}
                value={viewMode}
                onChange={(e) => onViewModeChange(e.target.value as any)}
              >
                <option value="default">Default</option>
                <option value="timeline">Timeline</option>
                <option value="parallel">Parallel</option>
              </select>
            </div>

            <div className="flex items-center space-x-3">
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCriticalPath}
                  onChange={(e) => onShowCriticalPathChange(e.target.checked)}
                  className={`rounded transition-all duration-200 ${
                    darkMode 
                      ? 'bg-gray-800 border-green-700 text-green-500 focus:ring-green-500/20' 
                      : 'border-green-300 text-green-600 focus:ring-green-300/30'
                  }`}
                />
                <span className={`text-xs whitespace-nowrap ${
                  darkMode ? 'text-green-300' : 'text-green-700'
                }`}>
                  Critical Path
                </span>
              </label>

              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showMetrics}
                  onChange={(e) => onShowMetricsChange(e.target.checked)}
                  className={`rounded transition-all duration-200 ${
                    darkMode 
                      ? 'bg-gray-800 border-green-700 text-green-500 focus:ring-green-500/20' 
                      : 'border-green-300 text-green-600 focus:ring-green-300/30'
                  }`}
                />
                <span className={`text-xs whitespace-nowrap ${
                  darkMode ? 'text-green-300' : 'text-green-700'
                }`}>
                  Metrics
                </span>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
